/**
 * ContentForge — Memory View
 *
 * Search + filter agent memories and activity log.
 * Two tabs: Memories (agent learnings) and Activity Log (timeline).
 * Stats bar showing today's summary + quick filter chips.
 */

import { html, nothing, type TemplateResult } from "lit";
import type { GatewayBrowserClient } from "../gateway.ts";

// ─── Types ──────────────────────────────────────────────────────────────

type MemoryEntry = {
  id: string;
  agentType: string;
  keywordId: string | null;
  title: string;
  content: string;
  createdAt: number;
};

type ActivityEntry = {
  id: string;
  timestamp: number;
  agentType: string;
  action: string;
  details: string;
  keywordId: string | null;
  status: "info" | "success" | "warning" | "error";
};

export type CfMemoryProps = {
  client: GatewayBrowserClient | null;
  onRefresh: () => void;
};

// ─── State ──────────────────────────────────────────────────────────────

let _memories: MemoryEntry[] = [];
let _allActivities: ActivityEntry[] = []; // unfiltered
let _activities: ActivityEntry[] = []; // filtered for display
let _loading = false;
let _tab: "memories" | "activity" = "activity";
let _searchQuery = "";
let _agentFilter = "";
let _timeFilter: "" | "today" | "week" = "";
let _statusFilter: "" | "error" | "warning" | "success" = "";
let _rerenderFn: (() => void) | null = null;
let _totalMemories = 0;
let _totalActivities = 0;

// ─── Constants ──────────────────────────────────────────────────────────

const AGENT_COLORS: Record<string, string> = {
  strategist: "#818cf8",
  writer: "#34d399",
  sourcer: "#fbbf24",
  linker: "#60a5fa",
  artist: "#f472b6",
  publisher: "#2dd4bf",
  arthur: "#c084fc",
  miner: "#fb923c",
  boss: "#ef4444",
  system: "#94a3b8",
};

const AGENT_LABELS: Record<string, string> = {
  strategist: "Strategist",
  writer: "Writer",
  sourcer: "Sourcer",
  linker: "Linker",
  artist: "Artist",
  publisher: "Publisher",
  arthur: "Arthur",
  miner: "Miner",
  boss: "Boss",
  system: "System",
};

const STATUS_ICONS: Record<string, string> = {
  info: "ℹ️",
  success: "✅",
  warning: "⚠️",
  error: "❌",
};

// ─── Data Loading ───────────────────────────────────────────────────────

async function loadMemories(client: GatewayBrowserClient | null) {
  if (!client) {
    return;
  }
  _loading = true;
  _rerenderFn?.();

  try {
    if (_searchQuery.trim()) {
      const res = await client.request<{ memories: MemoryEntry[]; total: number }>(
        "contentforge.memory.search",
        { query: _searchQuery, agentType: _agentFilter || undefined, limit: 100 },
      );
      _memories = res.memories ?? [];
      _totalMemories = res.total;
    } else {
      const res = await client.request<{ memories: MemoryEntry[]; total: number }>(
        "contentforge.memory.list",
        { agentType: _agentFilter || undefined, limit: 100 },
      );
      _memories = res.memories ?? [];
      _totalMemories = res.total;
    }
  } catch (err) {
    console.error("[Memory] Failed to load memories:", err);
    _memories = [];
  }

  _loading = false;
  _rerenderFn?.();
}

async function loadActivities(client: GatewayBrowserClient | null) {
  if (!client) {
    return;
  }
  _loading = true;
  _rerenderFn?.();

  try {
    const res = await client.request<{ entries: ActivityEntry[]; total: number }>(
      "contentforge.activity.list",
      { limit: 500 },
    );
    _allActivities = res.entries ?? [];
    _totalActivities = res.total;
    applyFilters();
  } catch (err) {
    console.error("[Memory] Failed to load activities:", err);
    _allActivities = [];
    _activities = [];
  }

  _loading = false;
  _rerenderFn?.();
}

function applyFilters() {
  let entries = _allActivities;

  // Agent filter
  if (_agentFilter) {
    entries = entries.filter((e) => e.agentType === _agentFilter);
  }

  // Time filter
  if (_timeFilter === "today") {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    entries = entries.filter((e) => e.timestamp >= todayStart.getTime());
  } else if (_timeFilter === "week") {
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    entries = entries.filter((e) => e.timestamp >= weekAgo);
  }

  // Status filter
  if (_statusFilter) {
    entries = entries.filter((e) => e.status === _statusFilter);
  }

  // Text search
  if (_searchQuery.trim()) {
    const q = _searchQuery.toLowerCase();
    entries = entries.filter(
      (e) => e.details.toLowerCase().includes(q) || e.action.toLowerCase().includes(q),
    );
  }

  _activities = entries;
}

function loadData(client: GatewayBrowserClient | null) {
  if (_tab === "memories") {
    void loadMemories(client);
  } else {
    void loadActivities(client);
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) {
    return "just now";
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days}d ago`;
  }
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function agentBadge(agentType: string): TemplateResult {
  const color = AGENT_COLORS[agentType] ?? "#94a3b8";
  const label = AGENT_LABELS[agentType] ?? agentType;
  return html`<span class="cf-mem__agent-badge" style="background: ${color}20; color: ${color}; border: 1px solid ${color}40">${label}</span>`;
}

// ─── Stats ──────────────────────────────────────────────────────────────

function computeStats(): { articles: number; keywords: number; errors: number; publishes: number } {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayTs = todayStart.getTime();

  const todayEntries = _allActivities.filter((e) => e.timestamp >= todayTs);

  return {
    articles: todayEntries.filter(
      (e) => e.action === "article_drafted" || e.action === "orchestrator.writer",
    ).length,
    keywords: todayEntries.filter(
      (e) =>
        e.action === "keywords.generated" ||
        e.action === "keywords.added" ||
        e.action === "orchestrator.miner",
    ).length,
    errors: todayEntries.filter((e) => e.status === "error").length,
    publishes: todayEntries.filter(
      (e) => e.action === "orchestrator.published" || e.action === "article.scheduled",
    ).length,
  };
}

function renderStatsBar() {
  const stats = computeStats();
  return html`
    <div class="cf-memory__stats">
      <div class="cf-memory__stat">
        <span class="cf-memory__stat-val">${stats.articles}</span>
        <span class="cf-memory__stat-lbl">Written Today</span>
      </div>
      <div class="cf-memory__stat">
        <span class="cf-memory__stat-val">${stats.publishes}</span>
        <span class="cf-memory__stat-lbl">Published</span>
      </div>
      <div class="cf-memory__stat">
        <span class="cf-memory__stat-val">${stats.keywords}</span>
        <span class="cf-memory__stat-lbl">Mined</span>
      </div>
      <div class="cf-memory__stat cf-memory__stat--${stats.errors > 0 ? "error" : "ok"}">
        <span class="cf-memory__stat-val">${stats.errors}</span>
        <span class="cf-memory__stat-lbl">Errors</span>
      </div>
    </div>
  `;
}

// ─── Quick Filter Chips ─────────────────────────────────────────────────

function renderQuickFilters(_client: GatewayBrowserClient | null) {
  const chip = (label: string, active: boolean, onClick: () => void) => html`
    <button
      class="cf-memory__chip ${active ? "cf-memory__chip--active" : ""}"
      @click=${onClick}
    >${label}</button>
  `;

  return html`
    <div class="cf-memory__chips">
      ${chip("All", !_timeFilter && !_statusFilter, () => {
        _timeFilter = "";
        _statusFilter = "";
        applyFilters();
        _rerenderFn?.();
      })}
      ${chip("Today", _timeFilter === "today", () => {
        _timeFilter = _timeFilter === "today" ? "" : "today";
        applyFilters();
        _rerenderFn?.();
      })}
      ${chip("This Week", _timeFilter === "week", () => {
        _timeFilter = _timeFilter === "week" ? "" : "week";
        applyFilters();
        _rerenderFn?.();
      })}
      ${chip("❌ Errors", _statusFilter === "error", () => {
        _statusFilter = _statusFilter === "error" ? "" : "error";
        applyFilters();
        _rerenderFn?.();
      })}
      ${chip("⚠️ Warnings", _statusFilter === "warning", () => {
        _statusFilter = _statusFilter === "warning" ? "" : "warning";
        applyFilters();
        _rerenderFn?.();
      })}
      ${chip("✅ Success", _statusFilter === "success", () => {
        _statusFilter = _statusFilter === "success" ? "" : "success";
        applyFilters();
        _rerenderFn?.();
      })}
    </div>
  `;
}

// ─── Render ─────────────────────────────────────────────────────────────

export function renderCfMemory(props: CfMemoryProps, rerender: () => void) {
  _rerenderFn = rerender;

  // Initial load
  if (_allActivities.length === 0 && _memories.length === 0 && !_loading) {
    loadData(props.client);
  }

  return html`
    <div class="cf-memory">
      <!-- Header -->
      <div class="cf-memory__header">
        <h2 class="cf-memory__title">🧠 Memory & Activity</h2>
        <div class="cf-memory__controls">
          <button
            class="cf-btn cf-btn--sm ${_tab === "activity" ? "cf-btn--primary" : "cf-btn--ghost"}"
            @click=${() => {
              _tab = "activity";
              loadData(props.client);
            }}
          >Activity Log</button>
          <button
            class="cf-btn cf-btn--sm ${_tab === "memories" ? "cf-btn--primary" : "cf-btn--ghost"}"
            @click=${() => {
              _tab = "memories";
              loadData(props.client);
            }}
          >Memories</button>
          <button class="cf-btn cf-btn--sm cf-btn--ghost" @click=${() => loadData(props.client)}>
            Refresh
          </button>
        </div>
      </div>

      <!-- Stats Bar (Activity tab only) -->
      ${_tab === "activity" && _allActivities.length > 0 ? renderStatsBar() : nothing}

      <!-- Quick Filter Chips (Activity tab only) -->
      ${_tab === "activity" ? renderQuickFilters(props.client) : nothing}

      <!-- Search + Filter -->
      <div class="cf-memory__filters">
        <input
          class="cf-memory__search"
          type="text"
          placeholder="${_tab === "memories" ? "Search memories…" : "Search activity…"}"
          .value=${_searchQuery}
          @input=${(e: Event) => {
            _searchQuery = (e.target as HTMLInputElement).value;
            if (_tab === "activity") {
              applyFilters();
              _rerenderFn?.();
            }
          }}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Enter") {
              loadData(props.client);
            }
          }}
        />
        <select
          class="cf-memory__agent-select"
          .value=${_agentFilter}
          @change=${(e: Event) => {
            _agentFilter = (e.target as HTMLSelectElement).value;
            if (_tab === "activity") {
              applyFilters();
              _rerenderFn?.();
            } else {
              loadData(props.client);
            }
          }}
        >
          <option value="">All Agents</option>
          ${Object.entries(AGENT_LABELS).map(
            ([key, label]) => html`<option value="${key}">${label}</option>`,
          )}
        </select>
        <span class="cf-memory__count">
          ${_tab === "memories" ? `${_memories.length} of ${_totalMemories}` : `${_activities.length} of ${_totalActivities}`} entries
        </span>
      </div>

      <!-- Loading -->
      ${
        _loading
          ? html`
              <div class="cf-loading">Loading…</div>
            `
          : nothing
      }

      <!-- Content -->
      ${!_loading && _tab === "activity" ? renderActivityList() : nothing}
      ${!_loading && _tab === "memories" ? renderMemoryList() : nothing}
    </div>
  `;
}

// ─── Activity Log ───────────────────────────────────────────────────────

function renderActivityList() {
  if (_activities.length === 0) {
    return html`
      <div class="cf-memory__empty">No activity entries found.</div>
    `;
  }

  // Group by date
  const groups = new Map<string, ActivityEntry[]>();
  for (const entry of _activities) {
    const dateKey = new Date(entry.timestamp).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(entry);
  }

  return html`
    <div class="cf-memory__list">
      ${[...groups.entries()].map(
        ([date, entries]) => html`
        <div class="cf-memory__date-group">
          <div class="cf-memory__date-header">${date} <span class="cf-memory__date-count">${entries.length}</span></div>
          ${entries.map(
            (entry) => html`
            <div class="cf-memory__activity-card cf-memory__activity-card--${entry.status}">
              <div class="cf-memory__activity-row">
                <span class="cf-memory__status-icon">${STATUS_ICONS[entry.status] ?? "•"}</span>
                ${agentBadge(entry.agentType)}
                <span class="cf-memory__action">${entry.action}</span>
                <span class="cf-memory__time">${timeAgo(entry.timestamp)}</span>
              </div>
              <div class="cf-memory__details">${entry.details}</div>
            </div>
          `,
          )}
        </div>
      `,
      )}
    </div>
  `;
}

// ─── Memories ───────────────────────────────────────────────────────────

function renderMemoryList() {
  if (_memories.length === 0) {
    return html`<div class="cf-memory__empty">No memories found${_searchQuery ? ` for "${_searchQuery}"` : ""}.</div>`;
  }

  return html`
    <div class="cf-memory__list">
      ${_memories.map(
        (mem) => html`
        <div class="cf-memory__card">
          <div class="cf-memory__card-header">
            ${agentBadge(mem.agentType)}
            <span class="cf-memory__card-title">${mem.title}</span>
            <span class="cf-memory__time">${timeAgo(mem.createdAt)}</span>
          </div>
          <div class="cf-memory__card-content">${mem.content}</div>
        </div>
      `,
      )}
    </div>
  `;
}
